import type { Manifest } from '@clawharbor/manifest';
import { render } from 'preact';
import { ApiError, createApi } from './api.js';
import { normalizeUrl } from './actions.js';
import type { Actions } from './actions.js';
import { ConsoleView } from './app.js';
import { Store } from './store.js';
import { applyTenantTokens, clearTenantTokens } from './tenant-theme.js';
import { copyText } from './ui/copy.js';
import type { PublishFailure } from './types.js';

/**
 * Wiring.
 *
 * One rule worth stating: the console never fabricates progress. The meter and
 * the ledger move only when a frame arrives, so a stalled backend looks stalled
 * instead of looking busy. For a product whose pitch is "we show you what we
 * could not verify", a fake progress animation would be the wrong first lie.
 */

function boot(container: HTMLElement): void {
  const api = createApi();
  const store = new Store(api.mode);

  /** Ticks the elapsed clock while a crawl is in flight. Not a progress fake. */
  let ticker: ReturnType<typeof setInterval> | null = null;
  let now = Date.now();
  let unsubscribeStream: (() => void) | null = null;
  let copyTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Move focus when the SCREEN changes, and only then.
   *
   * The console replaces its entire main region three times — landing, crawl,
   * result — with no navigation and therefore no browser-provided focus reset.
   * A keyboard or screen-reader user who pressed "Crawl it" would otherwise be
   * left on a button that no longer exists, with focus on <body> and no idea
   * that a page of new content had appeared beneath them. Repainting on every
   * SSE frame must not steal focus, so this fires on transition only.
   */
  let painted: string | null = null;
  const paint = (): void => {
    const screen = store.state.screen;
    render(ConsoleView({ state: store.state, actions, now }), container);
    const first = painted === null;
    if (screen === painted) return;
    painted = screen;
    // First paint is a page LOAD: the reader starts at the top of the document
    // on their own, and grabbing the field would skip the heading that says
    // what this page is. Only a return to the landing screen — "New crawl",
    // "Start over" — is a transition, and there the field is the destination.
    if (screen === 'landing') {
      if (!first) document.getElementById('site-url')?.focus();
      return;
    }
    // The sign-in step asks one question, and the field that answers it is the
    // destination; the heading above it is one line the reader just pressed a
    // button to reach.
    if (screen === 'signin') {
      requestAnimationFrame(() => document.getElementById('login-email')?.focus());
      return;
    }
    requestAnimationFrame(() => {
      const heading = container.querySelector<HTMLElement>('[data-focus-target]');
      heading?.focus({ preventScroll: true });
    });
  };

  const startTicker = (): void => {
    if (ticker) return;
    ticker = setInterval(() => {
      now = Date.now();
      store.notify();
    }, 250);
  };

  const stopTicker = (): void => {
    if (!ticker) return;
    clearInterval(ticker);
    ticker = null;
    now = Date.now();
  };

  api.whenFallback((reason) => {
    store.patch({ mode: 'demo' });
    // Loud, not silent: the header chip flips and the reason is on the ledger.
    console.warn(`[clawharbor/console] onboarding API unavailable (${reason}); replaying a recorded crawl.`);
  });

  async function collect(jobId: string): Promise<void> {
    try {
      const manifest = await api.manifest(jobId);
      applyTenantTokens(manifest);
      store.finish(manifest);
      void resolveGate(jobId, manifest);
    } catch (err) {
      store.fail(`The crawl finished but its manifest could not be read: ${message(err)}`);
    } finally {
      stopTicker();
      store.notify();
    }
  }

  /**
   * Who is asking, asked once per result screen.
   *
   * `GET /api/auth/me` is the one call that decides which gate a person sees,
   * so it is made when the result screen appears and not on every repaint — a
   * ticking clock or a copy button must not turn into a login poll. For a draft
   * the person says is their own, the server's claim record is read in the same
   * pass, because "publish is refused until the domain is verified" is only
   * honest if the gate knows whether it already has been. Both answers are
   * discarded if a new crawl has started underneath them.
   */
  async function resolveGate(jobId: string, manifest: Manifest): Promise<void> {
    const principal = await api.me();
    if (store.state.jobId !== jobId) return;
    store.patch({ principal, principalChecked: true });

    if (!principal || manifest.policy.ownership === 'third-party') return;
    const tenantId = store.state.tenantId;
    if (!tenantId) return;
    try {
      const claim = await api.ownership(tenantId);
      if (store.state.jobId !== jobId) return;
      store.patch({ claim });
    } catch (err) {
      // No record is the same answer as "no current claim"; the proofs will
      // say what is missing. A real error is left where the gate prints it.
      if (store.state.jobId !== jobId) return;
      if (!(err instanceof ApiError && err.status === 404)) store.patch({ challengeError: message(err) });
    }
  }

  /** The API's sentence and its own code for the refusal; never "publish failed". */
  function publishFailure(err: unknown): PublishFailure {
    if (err instanceof ApiError) return { message: err.message, code: err.code };
    return { message: message(err), code: null };
  }

  /**
   * The field renders its own `https://` prefix, so the value must never carry one.
   *
   * It did. Clicking an example called useExample with a full URL and the field
   * read `https://https://www.hep.hr` — and a human pasting a copied address hit
   * exactly the same thing, which is the more common path of the two. The prefix
   * is a promise that the scheme is already handled; honouring it means stripping
   * a scheme wherever the value enters, not only where the examples do.
   *
   * `submit()` still runs the value through normalizeUrl, so this is presentation
   * only and cannot change which origin gets crawled.
   */
  const withoutScheme = (value: string): string => value.replace(/^\s*https?:\/\//i, '');

  const actions: Actions = {
    setInput(value) {
      store.patch({ input: withoutScheme(value), inputError: null });
    },

    useExample(url) {
      store.patch({ input: withoutScheme(url), inputError: null });
      document.getElementById('site-url')?.focus();
    },

    submit() {
      if (store.state.starting) return;
      const parsed = normalizeUrl(store.state.input);
      if ('error' in parsed) {
        store.patch({ inputError: parsed.error });
        return;
      }

      // Signed out against a real server, the crawl is going to be refused, so
      // ask first rather than spending a round trip to be told. The server is
      // still the authority — see the `sign_in_required` branch below, which is
      // the path a session that expired since boot takes.
      if (api.mode === 'live' && store.state.principalChecked && !store.state.principal) {
        store.patch({ pendingUrl: parsed.url, screen: 'signin', starting: false, inputError: null });
        return;
      }

      store.patch({ starting: true, inputError: null });
      void (async () => {
        try {
          const { jobId, tenantId } = await api.start(parsed.url, store.state.ownership);
          store.beginJob(jobId, tenantId, parsed.url);
          store.patch({ mode: api.mode, starting: false });
          startTicker();

          unsubscribeStream = api.events(jobId, {
            onEvent: (e) => store.apply(e),
            onDone: () => {
              unsubscribeStream = null;
              void collect(jobId);
            },
            onError: (msg) => {
              unsubscribeStream = null;
              store.fail(msg);
              stopTicker();
            },
          });
        } catch (err) {
          // The server's own word for it, and the only authoritative one: a
          // session can expire between boot and this press, and the check above
          // would have waved that through.
          if (err instanceof ApiError && err.code === 'sign_in_required') {
            store.patch({
              pendingUrl: parsed.url,
              principal: null,
              screen: 'signin',
              starting: false,
              inputError: null,
            });
            return;
          }
          store.patch({ starting: false, inputError: message(err) });
        }
      })();
    },

    resume() {
      const url = store.state.resumeUrl;
      if (!url || store.state.starting) return;
      store.patch({ input: withoutScheme(url), resumeUrl: null });
      actions.submit();
    },

    cancelSignIn() {
      store.patch({ screen: 'landing', loginSent: null, loginError: null });
    },

    copy(id, text) {
      void copyText(text).then((ok) => {
        if (!ok) return;
        store.patch({ copied: id });
        if (copyTimer) clearTimeout(copyTimer);
        copyTimer = setTimeout(() => store.patch({ copied: null }), 1800);
      });
    },

    toggleAck() {
      store.patch({ acknowledged: !store.state.acknowledged, publishError: null });
    },

    setPublishSecret(value) {
      // Held on the client object for the request and in state only so the
      // controlled input keeps its value across repaints. Never persisted.
      api.setPublishSecret(value);
      store.patch({ publishSecret: value, publishError: null });
    },

    publish() {
      const { jobId, acknowledged, publishing } = store.state;
      if (!jobId || !acknowledged || publishing) return;
      store.patch({ publishing: true, publishError: null });
      void api
        .publish(jobId)
        .then((res) => {
          store.patch({ publishing: false, published: res });
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        })
        .catch((err: unknown) => {
          store.patch({ publishing: false, publishError: publishFailure(err) });
        });
    },

    setOwnership(value) {
      store.patch({ ownership: value });
    },

    setLoginEmail(value) {
      store.patch({ loginEmail: value, loginError: null });
    },

    login() {
      const email = store.state.loginEmail.trim();
      if (store.state.loginSending) return;
      // The same shape the server's own validator wants; a miss here saves a
      // round trip and says so in the field's own language.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        store.patch({ loginError: 'Enter the email address the link should go to.' });
        return;
      }
      store.patch({ loginSending: true, loginError: null, loginSent: null });
      void api
        .login(email, resumeLink(store.state.pendingUrl, store.state.ownership))
        .then((sent) => store.patch({ loginSending: false, loginSent: sent }))
        .catch((err: unknown) => store.patch({ loginSending: false, loginError: message(err) }));
    },

    requestProofs() {
      const { tenantId, challengeBusy } = store.state;
      if (!tenantId || challengeBusy) return;
      store.patch({ challengeBusy: true, challengeError: null, verification: null });
      void api
        .challenge(tenantId)
        .then((challenge) => store.patch({ challengeBusy: false, challenge }))
        .catch((err: unknown) => store.patch({ challengeBusy: false, challengeError: message(err) }));
    },

    verifyOwnership() {
      const { tenantId, verifying, challenge, claim } = store.state;
      if (!tenantId || verifying) return;
      // The challenge this page issued, or the one the server already has
      // open; with neither the server picks its own open challenge, if any.
      const challengeId = challenge?.challengeId ?? claim?.openChallenge?.id;
      store.patch({ verifying: true, challengeError: null });
      void api
        .verify(tenantId, challengeId)
        .then((verification) => {
          store.patch({ verifying: false, verification, publishError: null });
          // A hit is a current claim, and the gate reads that from the record.
          if (verification.verified) {
            void api
              .ownership(tenantId)
              .then((claim) => {
                if (store.state.tenantId === tenantId) store.patch({ claim });
              })
              .catch(() => {});
          }
        })
        .catch((err: unknown) => store.patch({ verifying: false, challengeError: message(err) }));
    },

    restart() {
      unsubscribeStream?.();
      unsubscribeStream = null;
      stopTicker();
      clearTenantTokens();
      store.reset();
      store.patch({ mode: api.mode });
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
  };

  store.subscribe(paint);

  /**
   * What a sign-in link brought back, if anything.
   *
   * The pending address rides in the query string and nowhere else. Not
   * `sessionStorage`: this product writes nothing to browser storage, and more
   * practically, the link is very often opened in a different browser from the
   * one that typed the URL — a phone, a mail client's webview — where per-tab
   * storage has nothing to say. The query string is the one channel that
   * survives the round trip.
   *
   * It is read into `resumeUrl`, which paints a button. It is NOT crawled. The
   * value came out of a URL somebody could have mailed, and a console that
   * crawled whatever arrived would let a stranger aim this host at a site of
   * their choosing on the reader's budget and the reader's account.
   */
  const search = new URLSearchParams(location.search);
  const carried = search.get('url');
  if (carried) {
    const parsed = normalizeUrl(carried);
    const ownership = search.get('ownership');
    if ('url' in parsed) {
      store.patch({
        resumeUrl: parsed.url,
        input: withoutScheme(parsed.url),
        ...(ownership === 'self-owned' || ownership === 'third-party' ? { ownership } : {}),
      });
    }
    // Whatever it said, take it out of the address bar: a reload must be inert,
    // and a resume band that reappeared after the crawl had started would be
    // offering to run it a second time.
    history.replaceState({}, '', location.pathname);
  }

  paint();

  // Who is here, asked once, for the rail's label alone. The publish gate asks
  // again per result screen (resolveGate) because by then the answer decides
  // what may be done, not just what a link is called; a stale "Dashboard" in
  // the rail costs a click, and `me()` already answers null for an unreachable
  // host or a console with no account service behind it.
  void api
    .me()
    .then((principal) => store.patch({ principal, principalChecked: true }))
    .catch(() => undefined);

  // A useful handle during a live demo, and the only global this app defines.
  (window as unknown as { __clawharborConsole?: unknown }).__clawharborConsole = { store, api, actions };
}

/**
 * Where the mailed link should land: back here, holding what was typed.
 *
 * A path, never a URL — the server validates it against an allowlist of its own
 * paths and drops anything else, so a value shaped like somebody's origin buys
 * nothing. The address travels as a parameter rather than as part of the path
 * because it is data the console reads, not a route the server serves.
 */
function resumeLink(pendingUrl: string | null, ownership: string): string | undefined {
  if (!pendingUrl) return undefined;
  const q = new URLSearchParams({ url: pendingUrl, ownership });
  return `/console/?${q.toString()}`;
}

function message(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function start(): void {
  const container = document.getElementById('console');
  if (!container) {
    console.error('[clawharbor/console] no #console container in the document');
    return;
  }
  // Drop the pre-boot placeholder before Preact takes over the subtree.
  container.replaceChildren();
  boot(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
